import { Component, effect, inject, input, output, signal, untracked } from '@angular/core';
import { applyEach, form, FormField, required, validate } from '@angular/forms/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    OrganizationSettingsWebGatewayType,
} from '@libs/types/src/organization/types/organization-settings/organization-settings-web-gateway.type';
import { TextInputComponent } from '../../../../../../../components/kit/input/text-input-v2/text-input.component';
import { SwitchInputComponent } from '../../../../../../../components/kit/input/switch-input-v2/switch-input.component';
import { ButtonComponent } from '../../../../../../../components/kit/button/button.component';
import { SaveButtonComponent } from '../../../../../../../components/kit/button/save-button.component';
import { contentGlobalErrors, contentGlobalSuccess } from '../../../../../content.error';

@Component({
    selector: 'app-organization-settings-web-gateway',
    templateUrl: './organization-settings-web-gateway.component.html',
    imports: [FormField, TranslatePipe, TextInputComponent, SwitchInputComponent, ButtonComponent, SaveButtonComponent],
})
export class OrganizationSettingsWebGatewayComponent {
    private readonly translate = inject(TranslateService);

    public readonly settings = input.required<OrganizationSettingsWebGatewayType>();
    public readonly save = output<OrganizationSettingsWebGatewayType>();

    protected readonly formData = signal<OrganizationSettingsWebGatewayType>({ integrations: [], tokens: {} });
    protected readonly formDef = form(this.formData, schema => {
        applyEach(schema.integrations, integration => {
            required(integration.name);
            validate(integration.name, ({ value }) => value().trim() ? null : {
                kind: 'required',
                message: this.translate.instant('organization-settings.webGateway.nameRequired'),
            });
        });
    });
    protected readonly newIntegration = signal({ name: '' });
    protected readonly newIntegrationForm = form(this.newIntegration, schema => {
        required(schema.name);
        validate(schema.name, ({ value }) => value().trim() ? null : {
            kind: 'required',
            message: this.translate.instant('organization-settings.webGateway.nameRequired'),
        });
    });

    constructor() {
        effect(() => {
            const settings = this.settings();
            untracked(() => this.formDef().reset({
                integrations: settings.integrations.map(integration => ({ ...integration })),
                tokens: { ...settings.tokens },
            }));
        });
    }

    protected addIntegration(): void {
        this.newIntegrationForm().markAsTouched();
        if (this.newIntegrationForm().invalid()) {
            this.newIntegrationForm.name().focusBoundControl();
            return;
        }

        const id = crypto.randomUUID();
        const token = this.generateToken();
        const name = this.newIntegration().name.trim();
        this.formData.update(value => ({
            integrations: [...value.integrations, { id, name, active: true }],
            tokens: { ...value.tokens, [id]: token },
        }));
        this.formDef().markAsDirty();
        this.newIntegrationForm().reset({ name: '' });
    }

    protected removeIntegration(id: string): void {
        this.formData.update(value => {
            const tokens = { ...value.tokens };
            delete tokens[id];
            return {
                integrations: value.integrations.filter(integration => integration.id !== id),
                tokens,
            };
        });
        this.formDef().markAsDirty();
    }

    protected regenerateToken(id: string): void {
        const token = this.generateToken();
        this.formData.update(value => ({ ...value, tokens: { ...value.tokens, [id]: token } }));
        this.formDef().markAsDirty();
    }

    protected async copyToken(id: string): Promise<void> {
        try {
            await navigator.clipboard.writeText(this.formData().tokens[id]);
            contentGlobalSuccess.next('organization-settings.webGateway.tokenCopied');
        } catch {
            contentGlobalErrors.next('organization-settings.webGateway.copyFailed');
        }
    }

    protected saveSettings(): void {
        this.formDef().markAsTouched();
        if (this.formDef().invalid()) return;

        const value = this.formData();
        this.save.emit({
            integrations: value.integrations.map(integration => ({ ...integration, name: integration.name.trim() })),
            tokens: { ...value.tokens },
        });
    }

    private generateToken(): string {
        return Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('');
    }

}
